import { DatePipe, registerLocaleData } from '@angular/common';
import {
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi
} from '@angular/common/http';
import localeBr from '@angular/common/locales/pt';
import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  ErrorHandler,
  inject,
  LOCALE_ID,
  provideZoneChangeDetection
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  TitleStrategy,
  withComponentInputBinding
} from '@angular/router';

import { provideAnalytics } from '@gruposaltaedu/liv-analytics';
import { provideNgIconLoader, withCaching } from '@ng-icons/core';
import {
  LOCAL_STORAGE_ENGINE,
  StorageOption,
  withNgxsStoragePlugin
} from '@ngxs/storage-plugin';
import { provideStore } from '@ngxs/store';
import { provideNgxMask } from 'ngx-mask';

import { environment } from '@env/environment';

import { routes } from './app.routes';
import { GlobalErrorHandlerService } from './core/errors/global-error-handler.service';
import { httpInterceptorProviders } from './core/interceptors';
import { MwTitleStrategy } from './core/navigation/mw-title.strategy';
import { encrypt, decrypt } from './core/security/state-cypher';
import { CustomerUIState } from './core/store/customer/customer-ui.state';
import { CustomerState } from './core/store/customer/customer.state';
import { KibanaState } from './core/store/kibana/kibana.state';
import {
  TOAST_CONFIG,
  ToastConfig
} from './core/ui/components/toast/toast-config';
import { BemobiCheckoutWebhookService } from './features/cart-checkout/services/bemobi-checkout-webhook.service';
import {
  BemobiCheckoutService,
  CheckoutOrchestratorService,
  EskolareCheckoutService
} from './features/cart-checkout/services/flows';
import { CartUIState } from './features/cart-checkout/store/cart/cart-ui.state';
import { CartState } from './features/cart-checkout/store/cart/cart.state';
import { CartCheckoutState } from './features/cart-checkout/store/cart-checkout/cart-checkout.state';
import { SuggestionState } from './features/cart-checkout/store/suggestion/suggestion.state';
import { MessageStickerState } from './features/home/store/messages-sticker/message-sticker.state';
import { ProductsState } from './features/home/store/products/products.state';
import { StudentState } from './features/student/store/student/student.state';
import { StudentSchoolsState } from './features/student/store/student-schools/student-schools.state';
import { StudentSessionState } from './features/student/store/student-session/student-session.state';

registerLocaleData(localeBr, 'pt-BR');

const PERSISTED_STORAGE_STATES = [
  CartState,
  CustomerState,
  StudentSessionState,
  SuggestionState
];

const STATES = [
  ...PERSISTED_STORAGE_STATES,
  KibanaState,
  StudentSchoolsState,
  MessageStickerState,
  StudentState,
  ProductsState,
  CartUIState,
  CustomerUIState,
  CartCheckoutState
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    { provide: TitleStrategy, useClass: MwTitleStrategy },
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideNgxMask({
      validation: false
    }),
    provideStore(
      [...STATES],
      withNgxsStoragePlugin({
        serialize: (value: Record<string, unknown>) =>
          environment.production ? encrypt(value) : JSON.stringify(value),
        deserialize: (value: string) =>
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          environment.production ? decrypt(value) : JSON.parse(value),
        keys: [
          // Estados que devem usar localStorage
          ...PERSISTED_STORAGE_STATES.map((key) => ({
            key,
            engine: LOCAL_STORAGE_ENGINE
          }))
        ],
        // Fallback para sessionStorage
        storage: StorageOption.SessionStorage,
        namespace: '@my-world-shop'
      })
    ),
    provideNgIconLoader((name) => {
      const http = inject(HttpClient);
      return http.get(`/assets/icons/${name}.svg`, { responseType: 'text' });
    }, withCaching()),
    DatePipe,
    httpInterceptorProviders,
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'BRL' },
    {
      provide: TOAST_CONFIG,
      useValue: {
        maxCount: 5,
        defaultPosition: 'top-center'
      } as ToastConfig
    },
    provideAnalytics({
      project: 'my-world-shop',
      baseUrl: environment.apiUrl
    }),
    CheckoutOrchestratorService,
    EskolareCheckoutService,
    BemobiCheckoutService,
    BemobiCheckoutWebhookService
  ]
};
